// Live fleet-run view (#59): consume the mothership's NATS→SSE bridge (#58) for
// one BYOVM run. The route STREAMS THE DURABLE `task_run_events` ROWS
// (backfill → tail → terminate), never the raw NATS wire message — the same
// shape `lib/sessionStream.ts` reads, so the loop is shared
// (`lib/appendLogStream.ts`) and only the address, the frame name and the row
// schema live here. What is genuinely this view's own is the payload parsers.

import {
  RunEventFrameSchema,
  type RunStage,
  RunStagePayloadSchema,
  SseEvent,
} from '@developerz/domain';
import { z } from 'zod';
import { useAppendLogStream } from './appendLogStream';

/**
 * One projected run event, as `GET /v1/fleet/runs/:runId/events` emits it.
 *
 * The schema is the SHARED wire contract (`@developerz/domain`
 * `wire/sse-frames.ts`). It was declared here AND as `RunEventDto` in
 * `apps/api/src/routes/fleet-events.ts` — two packages that cannot import each
 * other — and had already drifted: this side carried a `payload_present` default
 * the server had no counterpart for.
 */
export const runEventRowSchema = RunEventFrameSchema;

export type RunEventRow = ReturnType<typeof runEventRowSchema.parse>;

/**
 * The shared absorbed-parse seam for every `status`-kind payload reader below
 * (#4397's `local-pref.ts` needed a new one elsewhere and this repo's
 * `json-parse-guard` ratchet counts DEFINITIONS, not call sites — three
 * near-identical `try { JSON.parse } catch { return null }` bodies here were
 * the room to make, not the ceiling to raise). `null` for a non-`status` row
 * or a malformed body; callers that need more than "is it JSON" (the schema
 * shape, the `event` field) still own that themselves.
 */
function parseStatusPayload(ev: RunEventRow): unknown | null {
  if (ev.kind !== 'status') return null;
  try {
    return JSON.parse(ev.payload_json);
  } catch {
    return null;
  }
}

/**
 * A `run_stage` status sub-event (#298) rides as `kind: 'status'` with
 * `payload_json` holding `{event:'run_stage', stage, from?, attempt}` —
 * `RunStagePayloadSchema` (`@developerz/domain`) is the source of truth for
 * the shape and the `RUN_STAGES` ladder. Returns `null` for anything that
 * isn't a well-formed stage payload, including malformed JSON.
 */
export function parseRunStageEvent(ev: RunEventRow): { stage: RunStage; from?: RunStage } | null {
  const payload = parseStatusPayload(ev);
  if (payload === null) return null;
  const parsed = RunStagePayloadSchema.safeParse(payload);
  if (!parsed.success) return null;
  return parsed.data.from
    ? { stage: parsed.data.stage, from: parsed.data.from }
    : { stage: parsed.data.stage };
}

/**
 * A `hygiene_summary` status sub-event (apps/runner `hygiene/index.ts` +
 * `loop.ts`) rides as `kind: 'status'` with `payload_json` holding
 * `{event:'hygiene_summary', kind, enabled, dryRun, findings, actions,
 * bytesFreed, durationMs, errors}` — the exact `HygieneSummary` shape spread
 * onto the emit call. There is no cross-app schema to import (the runner is a
 * SIBLING app, never a dependency of this one — `docs/idea/monorepo.md`), so
 * this is deliberately LENIENT: it trusts only the fields this view renders
 * (counts + byte total), passing every finding/action through unparsed so an
 * unknown box-side kind never breaks the timeline.
 */
const hygieneSummaryPayloadSchema = z.object({
  event: z.literal('hygiene_summary'),
  enabled: z.boolean(),
  dryRun: z.boolean(),
  findings: z.array(z.unknown()),
  actions: z.array(z.unknown()),
  bytesFreed: z.number(),
  errors: z.array(z.unknown()),
});

export type HygieneSummaryEvent = z.infer<typeof hygieneSummaryPayloadSchema>;

/**
 * Parses a run event as a `hygiene_summary` sub-event, or `null` when it
 * isn't one (wrong kind, malformed JSON, or a payload shape this view
 * doesn't recognize) — same "return null, never throw" contract as
 * {@link parseRunStageEvent}.
 */
export function parseHygieneSummaryEvent(ev: RunEventRow): HygieneSummaryEvent | null {
  const payload = parseStatusPayload(ev);
  if (payload === null) return null;
  const parsed = hygieneSummaryPayloadSchema.safeParse(payload);
  return parsed.success ? parsed.data : null;
}

/**
 * The `status` payload's own `event` discriminator — `run_heartbeat`,
 * `git_token_resolved`, `babysit_checkout`, … — or `null` when the row is not a
 * `status` or carries none.
 *
 * Here with the other payload parsers because TWO views read it: `<RunEventTitle/>`
 * titles from it and `<FleetRunTimeline/>` filters on it. Prod carries 66,436
 * `status` rows across ~30 sub-shapes, so it decides what 87% of a timeline says.
 */
export function parseStatusEventName(ev: RunEventRow): string | null {
  const payload = parseStatusPayload(ev);
  if (typeof payload !== 'object' || payload === null) return null;
  const event = (payload as Record<string, unknown>)['event'];
  return typeof event === 'string' && event !== '' ? event : null;
}

/**
 * The most recent stage this run has entered, read off the tail of the
 * accumulated events (rows arrive in `seq` order). `null` before the run's
 * first `run_stage` event lands — callers render nothing for that case.
 */
export function latestRunStage(events: readonly RunEventRow[]): RunStage | null {
  for (let i = events.length - 1; i >= 0; i -= 1) {
    const ev = events[i];
    if (!ev) continue;
    const parsed = parseRunStageEvent(ev);
    if (parsed) return parsed.stage;
  }
  return null;
}

/**
 * Open the SSE stream for `runId` and expose the accumulating events, a
 * connection status and a `retry` that reconnects from a clean slate (#298). A
 * mid-stream recycle reconnects by itself, resuming from the last row seen.
 */
export function useFleetRunStream(runId: () => string, token: string | null) {
  return useAppendLogStream(runId, token, {
    path: (id) => `/v1/fleet/runs/${encodeURIComponent(id)}/events`,
    event: SseEvent.RunEvent,
    schema: runEventRowSchema,
  });
}
